import React, { useState, useEffect } from 'react';
import { getProductosStockCritico, exportarStockCriticoExcel } from '../../../services/ReportesStockMin';
import { getReporteCajaPorFechas, exportarCajaExcel } from '../../../services/ReportesCaja';
import { getInventarioGeneral, getInventarioFechaCorta, exportarInventarioExcel } from '../../../services/ReportesInventario';
import { getReporteVentasEmpleados, exportarEmpleadosExcel } from '../../../services/ReportesEmpleados';
import { getUsuarios } from '../../../services/UserService';

// Estilos
import '../../static/Global.css';
import '../../static/Home.css';
import '../../static/Aside.css';
import '../../static/Reportes.css';

function DashboardReportes() {
    const [productosCriticos, setProductosCriticos] = useState([]);
    const [datosCaja, setDatosCaja] = useState([]);
    const [datosInventario, setDatosInventario] = useState([]);
    const [datosEmpleados, setDatosEmpleados] = useState([]);
    const [listaUsuarios, setListaUsuarios] = useState([]); 
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(''); 
    
    const [loading, setLoading] = useState(true);
    const [reporteSeleccionado, setReporteSeleccionado] = useState('stock_critico');

    // Estados para el rango de fechas (Inicializados con el día de hoy)
    const hoyFormateado = new Date().toISOString().split('T')[0];
    const [fechaDesde, setFechaDesde] = useState(hoyFormateado);
    const [fechaHasta, setFechaHasta] = useState(hoyFormateado);

    // 1. Carga inicial unificada de Stock Crítico y de la lista de Usuarios
    useEffect(() => {
        const inicializarDatos = async () => {
            setLoading(true);
            try {
                const [stockData, usuariosData] = await Promise.all([
                    getProductosStockCritico(),
                    getUsuarios()
                ]);
                setProductosCriticos(stockData);
                setListaUsuarios(usuariosData);
            } catch (error) {
                console.error("Error en carga inicial:", error.message);
            } finally {
                setLoading(false);
            }
        };
        inicializarDatos();
    }, []); // Solamente corre al montar el componente

    // 2. Effect: Carga reactiva de Caja cuando cambia el reporte o las fechas
    useEffect(() => {
        if (reporteSeleccionado !== 'caja') return;

        const cargarCaja = async () => {
            setLoading(true);
            try {
                const data = await getReporteCajaPorFechas(fechaDesde, fechaHasta);
                setDatosCaja(data);
            } catch (error) {
                console.error("Error al cargar reporte de caja:", error.message);
                setDatosCaja([]);
            } finally {
                setLoading(false);
            }
        };

        cargarCaja();
    }, [reporteSeleccionado, fechaDesde, fechaHasta]);

    // 3. Effect: Carga reactiva de Inventario General
    useEffect(() => {
        if (reporteSeleccionado !== 'inventario_general') return;

        const cargarInventario = async () => {
            setLoading(true);
            try {
                const data = await getInventarioGeneral();
                setDatosInventario(data);
            } catch (error) {
                console.error("Error al cargar inventario:", error.message);
                setDatosInventario([]);
            } finally {
                setLoading(false);
            }
        };
        cargarInventario();
    }, [reporteSeleccionado]);

    // 4. Effect para cargar ventas por empleado corregido
    useEffect(() => {
        if (reporteSeleccionado !== 'empleados') return;

        const cargarEmpleados = async () => {
            setLoading(true);
            try {
                // 👈 CORREGIDO: Ahora sí le enviamos el usuario seleccionado al servicio
                const data = await getReporteVentasEmpleados(fechaDesde, fechaHasta, usuarioSeleccionado);
                setDatosEmpleados(data);
            } catch (error) {
                console.error("Error al cargar empleados:", error.message);
                setDatosEmpleados([]);
            } finally {
                setLoading(false);
            }
        };
        cargarEmpleados();
    }, [reporteSeleccionado, fechaDesde, fechaHasta, usuarioSeleccionado]); 

    const manejarDescargaFechaCorta = async () => {
        const productos = await getInventarioFechaCorta();
        exportarInventarioExcel(productos, "Fecha_Corta"); // Generará: Inventario_Fecha_Corta_2026-06-16.xlsx
    };

    // Manejador centralizado para la exportación a Excel
    const handleDescargarExcel = () => {
        try {
            if (reporteSeleccionado === 'stock_critico') {
                exportarStockCriticoExcel(productosCriticos);
            } else if (reporteSeleccionado === 'caja') {
                exportarCajaExcel(datosCaja, fechaDesde, fechaHasta);
            } else if (reporteSeleccionado === 'inventario_general') {
                exportarInventarioExcel(datosInventario);
            } else if (reporteSeleccionado === 'empleados') {
                exportarEmpleadosExcel(datosEmpleados, fechaDesde, fechaHasta);
            } else if (reporteSeleccionado === 'fecha_corta') {
                manejarDescargaFechaCorta();
            }
        } catch (error) {
            alert(error.message);
        }
    };

    // Determina si el botón de exportar debe estar bloqueado
    const isBotonDeshabilitado = loading || 
        (reporteSeleccionado === 'stock_critico' && productosCriticos.length === 0) ||
        (reporteSeleccionado === 'caja' && datosCaja.length === 0) ||
        (reporteSeleccionado === 'inventario_general' && datosInventario.length === 0) || 
        (reporteSeleccionado === 'empleados' && datosEmpleados.length === 0);

    return (
        <section className="main__content">
            <div className="table-container">
                <div className="table-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>GESTIÓN DE REPORTES</h2>
                </div>

                <div className="reportes-layout">
                    
                    {/* COLUMNA IZQUIERDA: Selectores de Tipo de Reporte */}
                    <div className="reportes-sidebar-left">
                        <div className="reportes-group">
                            <button 
                                type="button" 
                                className={`btn-vender-sidebar btn-reporte-selector ${reporteSeleccionado === 'stock_critico' ? 'activo' : ''}`}
                                onClick={() => setReporteSeleccionado('stock_critico')}
                            >
                                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '10px' }}></i>
                                Reporte de Stock Crítico
                            </button>

                            <button 
                                type="button" 
                                className={`btn-vender-sidebar btn-reporte-selector ${reporteSeleccionado === 'caja' ? 'activo' : ''}`}
                                onClick={() => setReporteSeleccionado('caja')}
                            >
                                <i className="fa-solid fa-cash-register" style={{ marginRight: '10px' }}></i>
                                Reporte de Caja
                            </button>

                            <button 
                                type="button" 
                                className={`btn-vender-sidebar btn-reporte-selector ${reporteSeleccionado === 'empleados' ? 'activo' : ''}`}
                                onClick={() => setReporteSeleccionado('empleados')}
                            >
                                <i className="fa-solid fa-user-tie" style={{ marginRight: '10px' }}></i>
                                Reporte de Ventas por empleado
                            </button>

                            <button 
                                type="button" 
                                className={`btn-vender-sidebar btn-reporte-selector ${reporteSeleccionado === 'inventario_general' ? 'activo' : ''}`}
                                onClick={() => setReporteSeleccionado('inventario_general')}
                            >
                                <i className="fa-solid fa-boxes-stacked" style={{ marginRight: '10px' }}></i>
                                Inventario General
                            </button>

                            <button 
                                type="button" 
                                className={`btn-vender-sidebar btn-reporte-selector ${reporteSeleccionado === 'fecha_corta' ? 'activo' : ''}`}
                                onClick={() => setReporteSeleccionado('fecha_corta')}
                            >
                                <i className="fa-solid fa-clock" style={{ marginRight: '10px' }}></i>
                                Inventario con Fecha Corta
                            </button>

                        </div>
                    </div>

                    {/* COLUMNA DERECHA: Segmentación y Configuración de Parámetros */}
                    <div className="reportes-content-right">
                        
                        <div className="reportes-group">
                            <label className="reportes-label">
                                {reporteSeleccionado === 'caja' || reporteSeleccionado === 'empleados' 
                                    ? "FILTRAR PARÁMETROS:" 
                                    : "CONTEXTO DEL REPORTE:"}
                            </label>
                            
                            {/* Rango de fechas dinámico compartido para Caja y Empleados */}
                            {(reporteSeleccionado === 'caja' || reporteSeleccionado === 'empleados') && (
                                <div className="reportes-fechas-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                                    
                                    {/* SELECTOR DE USUARIO: Solo visible en reporte de empleados */}
                                    {reporteSeleccionado === 'empleados' && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#555' }}>Seleccionar Empleado:</span>
                                            <select 
                                                value={usuarioSeleccionado}
                                                onChange={(e) => setUsuarioSeleccionado(e.target.value)}
                                                style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none', background: '#fff' }}
                                            >
                                                <option value="">-- Todos los Empleados --</option>
                                                {listaUsuarios.map((u) => (
                                                    <option key={u.id} value={u.nombre}>{u.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#555' }}>Desde:</span>
                                        <input 
                                            type="date" 
                                            className="input-custom-reporte"
                                            value={fechaDesde} 
                                            onChange={(e) => setFechaDesde(e.target.value)}
                                            style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#555' }}>Hasta:</span>
                                        <input 
                                            type="date" 
                                            className="input-custom-reporte"
                                            value={fechaHasta} 
                                            onChange={(e) => setFechaHasta(e.target.value)}
                                            style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none' }}
                                        />
                                    </div>
                                </div>
                            )}

                            {reporteSeleccionado === 'stock_critico' && (
                                <div className="reportes-info-clean" style={{ padding: '15px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeeba', color: '#856404' }}>
                                    <i className="fa-solid fa-circle-info" style={{ marginRight: '8px' }}></i>
                                    Ya puedes exportar tu reporte de Stock Crítico.
                                </div>
                            )}
                            
                            {reporteSeleccionado === 'inventario_general' && (
                                <div className="reportes-info-clean" style={{ padding: '15px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeeba', color: '#856404' }}>
                                    <i className="fa-solid fa-circle-info" style={{ marginRight: '8px' }}></i>
                                    Ya puedes exportar tu reporte de Inventario General.
                                </div>
                            )}

                            {reporteSeleccionado === 'fecha_corta' && (
                                <div className="reportes-info-clean" style={{ padding: '15px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeeba', color: '#856404' }}>
                                    <i className="fa-solid fa-circle-info" style={{ marginRight: '8px' }}></i>
                                    Ya puedes exportar tu reporte de Productos con Fecha Corta.
                                </div>
                            )}

                        </div>

                        {/* SECCIÓN ACCIONES: Botón de descarga unificado */}
                        <div className="reportes-actions-footer">
                            <button 
                                type="button" 
                                className={`btn-vender-sidebar btn-exportar-excel ${isBotonDeshabilitado ? 'deshabilitado' : ''}`}
                                onClick={handleDescargarExcel}
                                disabled={isBotonDeshabilitado}
                            >
                                <i className="fa-solid fa-file-excel" style={{ marginRight: '8px' }}></i> 
                                {loading ? "Procesando datos..." : "Exportar Reporte a Excel"}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
}

export default DashboardReportes;